import { ReactNode } from 'react'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import CharacterClassIcon from 'app/frontend/base/components/CharacterClassIcon'
import { imageUrl } from 'app/frontend/base/helpers'
import {
  getAllClasses,
  getAllSpecializations,
  getSpecialization,
  getSpecializationsByClass,
} from 'app/frontend/base/services/characterValues'
import { characterClasses } from 'app/frontend/types/custom/CharacterClass'
import type CharacterClass from 'app/frontend/types/custom/CharacterClass'
import { characterRoles } from 'app/frontend/types/custom/CharacterRole'
import { raidMarkerTypes } from 'app/frontend/types/custom/RaidMarker'
import type { IconKind } from 'app/frontend/types/custom/VisualPlan'
import SpellIcon from '../SpellIcon'
import {
  abilitySpellImageSrc,
  abilitySpellLabel,
} from './encounterAbilityIcons'
import {
  encounterCreatureImageSrc,
  encounterCreatureLabel,
  isEncounterGateway,
} from './encounterCreatureIcons'

export type IconKindItem = {
  value: string
  label: string
}

export type IconCanvasFrame = {
  corner: 'circle' | 'rounded'
}

export type IconKindConfig = {
  kind: IconKind
  label: string
  items: IconKindItem[]
  imageSrc: (value: string) => string
  renderToolbarIcon: (value: string) => ReactNode
  toolbarFramed?: boolean
  toolbarColumns?: number
  canvasFrame?: IconCanvasFrame
}

const capitalize = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1)

const roleImageFile = (value: string) => {
  return `${value.toLowerCase()}.png`
}

const blizzardIconSrc = (iconName: string) =>
  `https://render.worldofwarcraft.com/us/icons/36/${iconName}.jpg`

const allSpecializations = getAllSpecializations()
const allClasses = getAllClasses()

const classIdByName = Object.fromEntries(
  Object.entries(allClasses).map(([id, name]) => [name, Number(id)]),
)

export type SpecIconOption = {
  id: string
  name: string
  icon: string
}

export const specsForClassName = (className: string): SpecIconOption[] => {
  const classId = classIdByName[className]
  if (classId === undefined) return []
  return Object.entries(getSpecializationsByClass(classId)).map(
    ([id, spec]) => ({
      id,
      name: spec.name,
      icon: spec.icon,
    }),
  )
}

export const classNameForSpecId = (specId: string): string | undefined => {
  const spec = getSpecialization(specId)
  if (!spec || typeof spec.class_id !== 'number') return undefined
  return allClasses[String(spec.class_id)]
}

export const iconKinds: Record<IconKind, IconKindConfig> = {
  marker: {
    kind: 'marker',
    label: 'Markers',
    items: raidMarkerTypes.map((value) => ({
      value,
      label: capitalize(value),
    })),
    imageSrc: (value) => imageUrl(`markers/${value}.png`),
    renderToolbarIcon: (value) => (
      <img
        src={imageUrl(`markers/${value}.png`)}
        className='h-6 w-6 min-w-6 min-h-6'
      />
    ),
  },
  role: {
    kind: 'role',
    label: 'Roles',
    items: [
      ...characterRoles.map((value) => ({
        value,
        label: capitalize(value),
      })),
      { value: 'anyone', label: 'Anyone' },
    ],
    imageSrc: (value) =>
      value === 'anyone'
        ? imageUrl('bnet-temp.png')
        : imageUrl(`roles/${roleImageFile(value)}`),
    renderToolbarIcon: (value) => (
      <img
        src={
          value === 'anyone'
            ? imageUrl('bnet-temp.png')
            : imageUrl(`roles/${roleImageFile(value)}`)
        }
        className='h-6 w-6 min-w-6 min-h-6 rounded-full border border-[#666]'
        alt={value}
      />
    ),
    toolbarFramed: true,
    canvasFrame: { corner: 'circle' },
  },
  class: {
    kind: 'class',
    label: 'Classes',
    items: characterClasses.map((value) => ({
      value,
      label: value,
    })),
    imageSrc: (value) => imageUrl(`classes/${value.toLowerCase()}.jpeg`),
    renderToolbarIcon: (value) => (
      <CharacterClassIcon
        characterClass={value as CharacterClass}
        size='custom'
        className='h-7 w-7 min-h-7 min-w-7 border'
      />
    ),
    toolbarFramed: false,
    canvasFrame: { corner: 'rounded' },
  },
  spec: {
    kind: 'spec',
    label: 'Specializations',
    items: Object.entries(allSpecializations).map(([id, spec]) => ({
      value: id,
      label: spec.name,
    })),
    imageSrc: (value) => {
      const icon = getSpecialization(value)?.icon ?? 'inv_misc_questionmark'
      return blizzardIconSrc(icon)
    },
    renderToolbarIcon: (value) => {
      const icon = getSpecialization(value)?.icon ?? 'inv_misc_questionmark'
      return (
        <BlizzardIcon
          iconName={icon}
          size='medium'
          className='h-6 w-6 min-h-6 min-w-6'
        />
      )
    },
    toolbarFramed: false,
    canvasFrame: { corner: 'rounded' },
  },
  encounter: {
    kind: 'encounter',
    label: 'Encounter',
    items: [],
    imageSrc: (value) => encounterCreatureImageSrc(value),
    renderToolbarIcon: (value) => (
      <img
        src={encounterCreatureImageSrc(value)}
        className='h-14 w-14 min-h-14 min-w-14 rounded-full border border-[#666] object-contain'
        alt={encounterCreatureLabel(value)}
      />
    ),
    toolbarFramed: false,
    toolbarColumns: 3,
    canvasFrame: { corner: 'circle' },
  },
  ability: {
    kind: 'ability',
    label: 'Abilities',
    items: [],
    imageSrc: (value) => abilitySpellImageSrc(value),
    renderToolbarIcon: (value) => (
      <SpellIcon
        spellId={Number(value)}
        size='custom'
        className='h-7 w-7 min-h-7 min-w-7 rounded-md border border-[#666]'
      />
    ),
    toolbarFramed: false,
    canvasFrame: { corner: 'rounded' },
  },
}

export const iconKindOrder: IconKind[] = [
  'marker',
  'role',
  'class',
  'encounter',
  'ability',
]

export const getIconImageSrc = (
  kind: IconKind,
  value: string,
  encounterId?: number,
) => {
  if (kind === 'encounter') return encounterCreatureImageSrc(value, encounterId)
  return iconKinds[kind].imageSrc(value)
}

export const getIconLabel = (kind: IconKind, value: string) => {
  if (kind === 'encounter') return encounterCreatureLabel(value)
  if (kind === 'ability') return abilitySpellLabel(value)
  const item = iconKinds[kind].items.find((entry) => entry.value === value)
  return item?.label ?? value
}

export const getIconCanvasFrame = (kind: IconKind, value?: string) => {
  if (
    kind === 'encounter' &&
    value !== undefined &&
    isEncounterGateway(value)
  ) {
    return undefined
  }
  return iconKinds[kind].canvasFrame
}
